import React, { useState, useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { Label } from "@/components/fluid-ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/fluid-ui/popover";
import { Calendar } from "@/components/fluid-ui/calendar";
import { CalendarIcon } from "lucide-react";
import { compactCalendarClassNames } from "@/components/management/calendar-classnames";
import { format, addDays } from "date-fns";
import { ErrorAlert } from "@/components/ui/alert";

type ConfirmActionDialogProps = {
  title: string;
  description: string;
  onAction: (date?: string) => void;
  openDialog: boolean;
  setOpenDialog: (open: boolean) => void;
  errorMsg?: string;
  isLoading?: boolean;
  actionText?: string;
  showDatePicker?: boolean;
  defaultDate?: string;
};

export default function ConfirmActionDialog({
  title,
  description,
  onAction,
  openDialog,
  setOpenDialog,
  errorMsg,
  isLoading,
  actionText = "Delete",
  showDatePicker = false,
  defaultDate,
}: ConfirmActionDialogProps) {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    defaultDate ? new Date(defaultDate) : undefined,
  );
  const [calendarOpen, setCalendarOpen] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = addDays(today, 1);

  useEffect(() => {
    if (openDialog && showDatePicker && defaultDate) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const tomorrow = addDays(now, 1);
      const date = new Date(defaultDate);
      date.setHours(0, 0, 0, 0);
      Promise.resolve().then(() =>
        setSelectedDate(date < tomorrow ? tomorrow : date),
      );
    }
  }, [defaultDate, openDialog, showDatePicker]);

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setCalendarOpen(false);
  };

  const handleAction = () => {
    if (showDatePicker && selectedDate) {
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      onAction(formattedDate);
    } else {
      onAction();
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setSelectedDate(undefined);
    }
    setOpenDialog(open);
  };

  return (
    <>
      <Dialog open={openDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-sm rounded md:w-90">
          <DialogHeader className="flex flex-row justify-between">
            <DialogTitle className="text-md w-full text-left font-medium">
              {title}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-left text-sm text-gray-500">{description}</p>

            {showDatePicker && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-900">
                  {t("resume_date")}
                </Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "h-9 w-full justify-start border border-gray-300 bg-white px-3 py-2 text-left font-normal hover:bg-gray-50",
                        !selectedDate && "text-gray-500",
                      )}
                    >
                      {selectedDate ? (
                        format(selectedDate, "MM/dd/yyyy")
                      ) : (
                        <span>mm/dd/yyyy</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 text-gray-400" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0"
                    align="start"
                    sideOffset={4}
                    withPortal={false}
                    style={{ zIndex: 9999 }}
                  >
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateSelect}
                      disabled={(date) => date < minDate}
                      defaultMonth={selectedDate}
                      className="p-2"
                      classNames={compactCalendarClassNames}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {selectedDate && (
                  <p className="text-xs text-gray-500">
                    {"Your subscription will resume and you'll be charged on "}
                    <span className="font-semibold">
                      {format(selectedDate, "MMMM d, yyyy")}
                    </span>
                    .
                  </p>
                )}
              </div>
            )}
          </div>

          {errorMsg && <ErrorAlert message={errorMsg} />}

          <DialogFooter>
            <div className="flex w-full flex-row justify-between space-x-2">
              <DialogClose asChild>
                <Button
                  className="min-w-[70px] rounded bg-gray-50 p-3 text-gray-900 ring-1 ring-gray-300 hover:bg-gray-100"
                  onClick={() => handleDialogClose(false)}
                >
                  {t("cancel")}
                </Button>
              </DialogClose>
              <Button
                type="button"
                className={cn(
                  "bg-brand-primary text-brand-primary-text min-w-[70px] rounded p-3",
                )}
                onClick={handleAction}
                disabled={showDatePicker && !selectedDate}
              >
                {isLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-4 border-t-4 border-white border-t-transparent"></div>
                ) : (
                  actionText
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
